import { NavSet } from '@squareup/dex-types-shared-docs';

function normalizeDocsNavUrl(url: string | undefined): string {
  if (!url) {
    return '';
  }

  const pathname = url.split('#')[0]?.split('?')[0] || '';
  const withoutDocsPrefix = pathname.replace(/^\/docs(?=\/|$)/, '');
  return withoutDocsPrefix.startsWith('/')
    ? withoutDocsPrefix
    : `/${withoutDocsPrefix}`;
}

function sharedPathPrefixLength(a: string, b: string): number {
  const aParts = normalizeDocsNavUrl(a).split('/').filter(Boolean);
  const bParts = normalizeDocsNavUrl(b).split('/').filter(Boolean);
  let length = 0;

  while (aParts[length] && aParts[length] === bParts[length]) {
    length += 1;
  }

  return length;
}

function maxSharedPathPrefixLength(items: NavSet['items'], targetUrl: string) {
  let maxLength = 0;

  for (const item of items) {
    if (item.type === 'link' || item.type === 'subcategory') {
      maxLength = Math.max(
        maxLength,
        sharedPathPrefixLength(item.url || '', targetUrl)
      );
    }
    if (item.type === 'subcategory') {
      maxLength = Math.max(
        maxLength,
        maxSharedPathPrefixLength(item.items, targetUrl)
      );
    }
  }

  return maxLength;
}

// Walk the navSet to find which top-level tab contains the given slug, so
// the MDX path's `leftNavId` matches the tab id from the Contentful nav
// snapshot even though Contentful nav URLs omit the `/docs` prefix. Falls
// back to a best-prefix tab match for pages that exist in MDX but are not
// linked directly in the Contentful left nav.
//
// The caller passes the *filtered* navSet, so a page whose only nav entry is
// unpublished resolves the same way as a page missing from the nav entirely.
// When even the prefix match fails this returns the slug's first path segment,
// which is not a real tab id — `useLeftNavItemsForPage` then finds nothing and
// the page renders without a sidebar. `left-nav-overrides.json` exists to pin a
// tab for those pages; `left-nav-resolver.unit.spec.ts` fails if a manifest slug
// reaches that state without an override.
function findTabIdForSlug(navSet: NavSet, slug: string): string {
  const targetUrl = `/${slug}`;
  const containsUrl = (items: NavSet['items'], url: string): boolean =>
    items.some((item) => {
      if (item.type === 'link' || item.type === 'subcategory') {
        if (normalizeDocsNavUrl(item.url) === normalizeDocsNavUrl(url)) {
          return true;
        }
      }
      if (item.type === 'subcategory') {
        return containsUrl(item.items, url);
      }
      return false;
    });

  let bestPrefixMatch: string | undefined;
  let bestPrefixLength = 0;
  for (const tab of navSet.items) {
    if (
      tab.type === 'subcategory' &&
      (normalizeDocsNavUrl(tab.url) === normalizeDocsNavUrl(targetUrl) ||
        containsUrl(tab.items, targetUrl))
    ) {
      return tab.id;
    }
    if (tab.type === 'subcategory') {
      const prefixLength = maxSharedPathPrefixLength(tab.items, targetUrl);
      if (prefixLength > bestPrefixLength) {
        bestPrefixMatch = tab.id;
        bestPrefixLength = prefixLength;
      }
    }
  }
  if (bestPrefixMatch) {
    return bestPrefixMatch;
  }
  // `slug.split('/')[0]` is typed as `string | undefined` under
  // `noUncheckedIndexedAccess`; use `slice` to preserve the `string` return.
  const slashIdx = slug.indexOf('/');
  return slashIdx >= 0 ? slug.slice(0, slashIdx) : slug;
}

// True when the walk landed on a real top-level tab. The walk's last resort is
// the slug's first path segment, which usually is not a tab id.
function isResolvedTabId(navSet: NavSet, tabId: string): boolean {
  return navSet.items.some(
    (item) => item.type === 'subcategory' && item.id === tabId
  );
}

export { findTabIdForSlug, isResolvedTabId, normalizeDocsNavUrl };
